/**
 * OrchestrationStatus - Displays current AI processing status during streaming
 *
 * Matches the Figma ReasoningTrigger design: logo icon + shimmer "Thinking" text
 * + chevron to expand step details.
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from '@/i18n'
import type { AgentId } from '../../types'
import { AGENTS, DEFAULT_AGENT } from '../../constants'

export type OrchestrationStatusType = 'queuing' | 'analyzing' | 'planning' | 'retrieving' | 'generating'

export interface OrchestrationDocument {
  name: string
  id?: string
}

export interface OrchestrationStatusData {
  status: OrchestrationStatusType
  description: string
  documents?: OrchestrationDocument[]
}

interface OrchestrationStatusProps {
  agentId?: AgentId
  status: OrchestrationStatusType
  description: string
  documents?: OrchestrationDocument[]
}

// Status label translation-key + default-value mapping
const STATUS_LABELS: Record<OrchestrationStatusType, { key: string; defaultValue: string }> = {
  queuing: { key: 'chatv2:orchestration.label_queuing', defaultValue: 'Getting started' },
  analyzing: { key: 'chatv2:orchestration.label_analyzing', defaultValue: 'Analyzing' },
  planning: { key: 'chatv2:orchestration.label_planning', defaultValue: 'Reviewing' },
  retrieving: { key: 'chatv2:orchestration.label_retrieving', defaultValue: 'Gathering info' },
  generating: { key: 'chatv2:orchestration.label_generating', defaultValue: 'Writing' },
}

export function OrchestrationStatus({
  agentId = DEFAULT_AGENT,
  status,
  description,
  documents = [],
}: OrchestrationStatusProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [showAllDocs, setShowAllDocs] = useState(false)
  const agent = AGENTS[agentId] || AGENTS[DEFAULT_AGENT]
  const statusEntry = STATUS_LABELS[status]
  const statusLabel = statusEntry
    ? t(statusEntry.key, { defaultValue: statusEntry.defaultValue })
    : t('chatv2:orchestration.label_working', { defaultValue: 'Working' })

  // Show only first doc, rest behind "..."
  const visibleDocs = showAllDocs ? documents : documents.slice(0, 1)
  const hiddenCount = documents.length - 1

  return (
    <div className="flex flex-col gap-2 w-full max-w-3xl">
      {/* Thinking trigger row — matches Figma ReasoningTrigger */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        <img
          src="/images/intuai-logo-icon.svg"
          alt=""
          className="w-4 h-4 flex-shrink-0"
          aria-hidden="true"
        />
        <span className="text-sm font-medium thinking-shimmer">
          {t('chatv2:messages.thinking_short', { defaultValue: 'Thinking' })}
        </span>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="ml-6 flex flex-col gap-2">
          {/* Agent + status */}
          <div className="flex items-center gap-2">
            <img
              src={agent.avatarUrl}
              alt={agent.name}
              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {statusLabel}
            </span>
            <span className="text-sm text-gray-400 dark:text-gray-500">
              — {description}
            </span>
          </div>

          {/* Document chips */}
          {documents.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {visibleDocs.map((doc, index) => (
                <span
                  key={doc.id || index}
                  className="px-2 py-0.5 bg-gray-100 dark:bg-dark-accent rounded text-xs text-gray-600 dark:text-gray-400"
                >
                  {doc.name}
                </span>
              ))}
              {hiddenCount > 0 && !showAllDocs && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAllDocs(true) }}
                  className="px-2 py-0.5 bg-gray-100 dark:bg-dark-accent hover:bg-gray-200 dark:hover:bg-dark-border rounded text-xs text-gray-500 dark:text-dark-text-muted transition-colors"
                >
                  {t('chatv2:documents.show_more', { count: hiddenCount, defaultValue: '+{{count}} more' })}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default OrchestrationStatus
